import React, { memo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { IHttpRequestConfig } from "@/browser";
import { Fetcher, IFetcherRenderProps } from "@/components/Fetcher";
import { Section } from "@/components/Section";
import { IWithUserContextProps, UserContext, withUserContext } from "@/components/User";
import { MEDIA_SIZE_MD } from "@/core/media";
import { fetchConsultantProfile, IConsultantProfile } from "@/data/consultant-profiles";
import { IWithMessagesActions, withMessagesActions } from "@/eworkitComponents";
import { Col, Flex, Row } from "@/eworkitComponents";
import { withRouteProps } from "@/infrastructure/Route";
import { Container } from "@/views/components/Container";
import { PageHeader } from "@/views/components/Page";
import { useToasts } from "@/views/components/Toast/useToasts";
import { EditableAboutMeSection } from "@/views/ConsultantProfiles/Detail/AboutMeSection";
import { EditableAttachmentsSection } from "@/views/ConsultantProfiles/Detail/AttachmentsSection";
import { EditableRoleSection } from "@/views/ConsultantProfiles/Detail/RoleSection";
import { EditableWorkStylesSection } from "@/views/ConsultantProfiles/Detail/WorkStylesSection";
import { IWithHttpErrorProps, withHttpError } from "@/views/Errors";

import { ConsultantProfileCompletenessWidget } from "../Completeness";
import { transformRouteProps } from "../selectors";
import { IConsultantProfileIdDetailRouteProps } from "../types";
import { EditableAvailabilitySection } from "./AvailabilitySection";
import { EditableConnectionContactSection } from "./ContactSection";
import { EditableCvSection } from "./CvSection";
import { EditableGeneralSection } from "./GeneralSection";
import { EditableLanguagesSection } from "./LanguagesSection";
import { NewSuggestedDataSection } from "./NewSuggestedDataSection";
import { EditableSettingsSection } from "./SettingsSection";
import { EditableSkillsSection } from "./SkillsSection";
import { RecommendedJobsWidget } from "./Widgets";

interface Props
  extends IFetcherRenderProps<IConsultantProfile>,
    IWithUserContextProps,
    IWithHttpErrorProps,
    IWithMessagesActions {}

export const ConsultantProfileDetailRendererComponent = memo((props: Props) => {
  const { payload, updatePayload, updateContextUserProfile, hasConsultantProfile, hasUserRoleInCompany, fetch } = props;

  const isMediumScreen = useMedia(MEDIA_SIZE_MD);
  const { t } = useTranslation("session");

  const { addToast } = useToasts();

  if (!payload) {
    return null;
  }

  const isOwner = hasConsultantProfile(payload.id);
  const isAdmin = payload.sharedProfile ? hasUserRoleInCompany("ADMIN", payload.sharedProfile.company.id) : false;
  const isManager = payload.sharedProfile
    ? isAdmin || hasUserRoleInCompany("MANAGER", payload.sharedProfile.company.id)
    : false;

  const handleProfileUpdate = (profile: IConsultantProfile, skipMessage?: boolean) => {
    if (payload.id === profile.contactPerson.id) {
      // when phone is update, update corresponding contactPerson.phone as well
      // otherwise the data is not consistent until the page reload
      profile.contactPerson.phone = profile.phone;
      updateContextUserProfile(profile);
    }
    updatePayload(profile);

    if (!skipMessage) {
      addToast({ text: t("CONSULTANT_PROFILES.DETAIL.SUCCESS_MESSAGE"), type: "success" });
    }
  };

  return (
    <Container testName="consultant-profile-page">
      <Row direction={isMediumScreen ? "column" : "row"}>
        <Col width={{ xxs: 12 }}>
          <Flex direction="column" innerMargin="xl">
            {payload.newSuggestedData && (
              <NewSuggestedDataSection profile={payload} onProfileUpdate={handleProfileUpdate} />
            )}
            <PageHeader text={t("CONSULTANT_PROFILES.DETAIL.PAGE_HEADER")} margin={[false]} />
          </Flex>
        </Col>

        <Col width={{ md: 8, xxs: 12 }}>
          <Flex direction="column" innerMargin="sm">
            <EditableGeneralSection record={payload} onUpdate={handleProfileUpdate} afterUpdate={fetch} />

            <EditableAvailabilitySection record={payload} onUpdate={handleProfileUpdate} />

            <EditableCvSection record={payload} onUpdate={handleProfileUpdate} />

            <Section borderRadius padding={["lg"]}>
              <Flex direction="column" innerBorder>
                <EditableRoleSection record={payload} onUpdate={handleProfileUpdate} />
                <EditableSkillsSection record={payload} onUpdate={handleProfileUpdate} />
                <EditableLanguagesSection record={payload} onUpdate={handleProfileUpdate} />
              </Flex>
            </Section>

            <Section borderRadius padding={["lg"]}>
              <EditableWorkStylesSection record={payload} onUpdate={handleProfileUpdate} />
            </Section>

            <EditableAboutMeSection isOwner={isOwner} record={payload} onUpdate={handleProfileUpdate} />

            <EditableAttachmentsSection record={payload} onUpdate={handleProfileUpdate} />

            <EditableConnectionContactSection isManager={isManager} record={payload} onUpdate={handleProfileUpdate} />

            <EditableSettingsSection
              isAdmin={isAdmin}
              isManager={isManager}
              isOwner={isOwner}
              record={payload}
              onUpdate={handleProfileUpdate}
            />
          </Flex>
        </Col>

        <Col width={{ md: 4, xxs: 12 }}>
          <Flex direction="column" innerMargin="md">
            <ConsultantProfileCompletenessWidget profile={payload} onUpdate={handleProfileUpdate} />
            <RecommendedJobsWidget record={payload} />
          </Flex>
        </Col>
      </Row>
    </Container>
  );
});

export const ConsultantProfileDetailRenderer = withHttpError(
  withMessagesActions(withUserContext(ConsultantProfileDetailRendererComponent)),
);

interface IConsultantProfileDetailComponentProps extends IConsultantProfileIdDetailRouteProps {}

const ConsultantProfileDetailComponent: React.FC<IConsultantProfileDetailComponentProps> = ({ routeProps }) => {
  const { contextCompany } = useContext(UserContext);
  const handleFetch = (_: any, httpConfig: IHttpRequestConfig) => {
    return fetchConsultantProfile(
      {
        companyId: contextCompany && contextCompany.id,
        consultantProfileId: routeProps.consultantProfileId,
      },
      httpConfig,
    );
  };

  return (
    <Fetcher key={routeProps.consultantProfileId} fetchData={handleFetch}>
      {state => <ConsultantProfileDetailRenderer {...state} />}
    </Fetcher>
  );
};

export const ConsultantProfileDetail = withRouteProps(ConsultantProfileDetailComponent, transformRouteProps);
